import { useFilterStore } from "@/stores/filter-store";
import { toast } from "@/lib/toast";

/**
 * Apply a field-debate camp as the active paper-ID scope for the given concept.
 * Writes to useFilterStore and fires a toast. Pure side-effect helper, extracted
 * from FieldDebateView's default handleCampClick branch so it can be unit-tested
 * without a full React / Router / ReactFlow render tree.
 */
export function applyCampScope(
  camp: { paper_ids: string[]; label: string },
  conceptId: string,
): void {
  useFilterStore
    .getState()
    .setPaperIdScope(conceptId, camp.paper_ids, camp.label, "field-debate-camp");
  toast.info(
    `Reading: ${camp.label} · ${camp.paper_ids.length} papers. Existing filters cleared.`,
  );
}

/**
 * Apply field-debate bridge papers as the active paper-ID scope for the given concept.
 * Mirrors applyCampScope but uses kind "field-debate-bridges" and a fixed "Bridge papers" label.
 */
export function applyBridgesScope(
  bridgePaperIds: string[],
  conceptId: string,
): void {
  useFilterStore
    .getState()
    .setPaperIdScope(conceptId, bridgePaperIds, "Bridge papers", "field-debate-bridges");
  toast.info(
    `Reading: Bridge papers · ${bridgePaperIds.length} papers. Existing filters cleared.`,
  );
}

/**
 * Apply the union of two tension camps as the active paper-ID scope for the given concept.
 * Deduplicates paper_ids across both camps and uses kind "field-debate-tension".
 */
export function applyTensionScope(
  campA: { id: string; label: string; paper_ids: string[] },
  campB: { id: string; label: string; paper_ids: string[] },
  conceptId: string,
): void {
  const union = Array.from(new Set([...campA.paper_ids, ...campB.paper_ids]));
  const label = `${campA.label} × ${campB.label}`;
  useFilterStore
    .getState()
    .setPaperIdScope(conceptId, union, label, "field-debate-tension");
  toast.info(
    `Reading: ${label} · ${union.length} papers. Existing filters cleared.`,
  );
}
